// ============================================================
// Fee Structure Page
// ============================================================

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Chip, IconButton, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Dialog, DialogContent, DialogActions, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Plus, Edit2, Trash2, Search } from 'lucide-react';
import { PageHeader } from '@/components/navigation/PageHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { usePageMeta } from '@/hooks/usePageMeta';
import { tokens } from '@/theme/tokens';
import { useFeeStructures, useUpdateFeeStructure, useDeleteFeeStructure } from '@/hooks/useFeeStructure';
import { useSchool } from '@/hooks/useSchool';
import { academicSessionsApi } from '@/api/academicSessions.api';
import { studentsApi } from '@/api/students.api';
import { PATHS } from '@/router/paths';
import toast from 'react-hot-toast';

export function FeeStructurePage() {
  usePageMeta('Fee Structure', [
    { label: 'Fees', path: '/fees' },
    { label: 'Structure' },
  ]);

  const navigate = useNavigate();
  const { schoolId } = useSchool();
  const { data: feeStructures = [], isLoading } = useFeeStructures();
  const { mutate: updateFeeStructure, isPending: isUpdating } = useUpdateFeeStructure();
  const { mutate: deleteFeeStructure, isPending: isDeleting } = useDeleteFeeStructure();

  // Reference data for resolving IDs to names
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSession, setFilterSession] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Edit Dialog State
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    academic_year_id: '',
    class_id: '',
    section_id: '',
    is_default: true,
    is_active: true,
  });
  const [editSections, setEditSections] = useState([]);

  // Delete Dialog State
  const [deleteId, setDeleteId] = useState(null);

  // Components & Installments Modal State
  const [selectedStructureForComponents, setSelectedStructureForComponents] = useState(null);

  // Load Sessions and Classes
  useEffect(() => {
    if (schoolId) {
      academicSessionsApi
        .getAllBySchool(schoolId)
        .then((res) => {
          const list = Array.isArray(res) ? res : res?.data || [];
          setSessions(list);
        })
        .catch(console.error);

      studentsApi
        .getClassesSections()
        .then((res) => {
          const list = res?.data || (Array.isArray(res) ? res : []);
          setClasses(list);
        })
        .catch(console.error);
    }
  }, [schoolId]);

  // Lookup maps for fast name resolution
  const sessionMap = useMemo(() => {
    const map = {};
    sessions.forEach((s) => {
      map[s.id] = s.session_name || s.name || `${s.start_date || ''} - ${s.end_date || ''}`;
    });
    return map;
  }, [sessions]);

  const classMap = useMemo(() => {
    const map = {};
    classes.forEach((c) => {
      map[c.id || c.class_id] = c.class_name || c.name;
    });
    return map;
  }, [classes]);

  const sectionMap = useMemo(() => {
    const map = {};
    classes.forEach((c) => {
      (c.sections || []).forEach((sec) => {
        map[sec.id || sec.section_id] = sec.section_name || sec.name;
      });
    });
    return map;
  }, [classes]);

  // Filtered List
  const filteredStructures = useMemo(() => {
    return feeStructures.filter((item) => {
      const matchSearch =
        !searchTerm ||
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchSession = !filterSession || item.academic_year_id === filterSession;
      const matchClass = !filterClass || item.class_id === filterClass;
      const matchStatus =
        !filterStatus ||
        (filterStatus === 'ACTIVE' && item.is_active) ||
        (filterStatus === 'INACTIVE' && !item.is_active);

      return matchSearch && matchSession && matchClass && matchStatus;
    });
  }, [feeStructures, searchTerm, filterSession, filterClass, filterStatus]);

  // Edit modal handlers
  const handleOpenEdit = (row) => {
    setEditingItem(row);
    const selectedClass = classes.find((c) => (c.id || c.class_id) === row.class_id);
    const sections = selectedClass?.sections || [];
    setEditSections(sections);

    setEditForm({
      name: row.name || '',
      description: row.description || '',
      academic_year_id: row.academic_year_id || '',
      class_id: row.class_id || '',
      section_id: row.section_id || '',
      is_default: row.is_default ?? true,
      is_active: row.is_active ?? true,
    });
  };

  const handleEditClassChange = (classId) => {
    const selectedClass = classes.find((c) => (c.id || c.class_id) === classId);
    const sections = selectedClass?.sections || [];
    setEditSections(sections);
    setEditForm((prev) => ({
      ...prev,
      class_id: classId,
      section_id: sections.length > 0 ? sections[0].id || sections[0].section_id : '',
    }));
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!editingItem) return;

    updateFeeStructure(
      { id: editingItem.id, data: editForm },
      {
        onSuccess: () => {
          toast.success('Fee Structure updated successfully!');
          setEditingItem(null);
        },
        onError: (err) => {
          toast.error(err.message || 'Failed to update fee structure');
        },
      }
    );
  };

  const handleDeleteConfirm = () => {
    if (!deleteId) return;
    deleteFeeStructure(deleteId, {
      onSuccess: () => {
        toast.success('Fee Structure deleted successfully!');
        setDeleteId(null);
      },
      onError: (err) => {
        toast.error(err.message || 'Failed to delete fee structure');
      },
    });
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Fee Structure Name',
      flex: 2.8,
      minWidth: 260,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', py: 0.5, pr: 2, overflow: 'hidden' }}>
          <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
            {params.value || 'Untitled Structure'}
          </Typography>
          {params.row.description && (
            <Tooltip title={params.row.description} placement="top-start" arrow>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  fontSize: '0.75rem',
                  lineHeight: 1.2,
                  cursor: 'pointer',
                }}
              >
                {params.row.description}
              </Typography>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'academic_year_id',
      headerName: 'Academic Year',
      width: 210,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {sessionMap[params.value] || params.value || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'class_section',
      headerName: 'Class & Section',
      width: 150,
      valueGetter: (params, row) => {
        const cls = classMap[row?.class_id] || row?.class_id || '—';
        const sec = sectionMap[row?.section_id] || row?.section_id || '';
        return sec ? `${cls} (${sec})` : cls;
      },
      renderCell: (params) => {
        const cls = classMap[params.row?.class_id] || params.row?.class_id || '—';
        const sec = sectionMap[params.row?.section_id] || params.row?.section_id || '';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: '100%' }}>
            <Chip
              label={cls}
              size="small"
              color="primary"
              variant="outlined"
            />
            {sec && (
              <Chip
                label={sec}
                size="small"
                variant="outlined"
                color="default"
              />
            )}
          </Box>
        );
      },
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 80,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.value ? 'Active' : 'Inactive'}
            size="small"
            color={params.value ? 'success' : 'error'}
            sx={{ fontWeight: 500 }}
          />
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 95,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isDeleteAllowed = params.row?.is_delete_allowed !== false;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Tooltip title="Edit Structure">
              <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); navigate(`/fees/structure/edit/${params.row.id}`); }}>
                <Edit2 size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title={isDeleteAllowed ? "Delete Structure" : "This fee structure is already assigned, it cannot be deleted"}>
              <Box
                component="span"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isDeleteAllowed) {
                    toast.error('This fee structure is already assigned, it cannot be deleted');
                  } else {
                    setDeleteId(params.row.id);
                  }
                }}
                sx={{ display: 'inline-flex', cursor: !isDeleteAllowed ? 'not-allowed' : 'pointer' }}
              >
                <IconButton
                  size="small"
                  color="error"
                  disabled={!isDeleteAllowed}
                  sx={{
                    pointerEvents: !isDeleteAllowed ? 'none' : 'auto'
                  }}
                >
                  <Trash2 size={16} />
                </IconButton>
              </Box>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ pb: 6 }}>
      <PageHeader
        title="Fee Structures"
        subtitle="Manage and define fee structures per class and academic session."
        action={
          <Button
            variant="contained"
            startIcon={<Plus size={20} />}
            onClick={() => navigate(PATHS.FEES_STRUCTURE_ADD)}
          >
            Create Fee
          </Button>
        }
      />

      <WidgetContainer noPadding sx={{ mt: 3 }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${tokens.colors.border}`, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search by structure name or description..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 280 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color={tokens.colors.text.secondary} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Academic Year</InputLabel>
            <Select
              value={filterSession}
              label="Academic Year"
              onChange={(e) => setFilterSession(e.target.value)}
            >
              <MenuItem value=""><em>All Sessions</em></MenuItem>
              {sessions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.session_name || s.name || `${s.start_date || ''} - ${s.end_date || ''}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Class</InputLabel>
            <Select
              value={filterClass}
              label="Class"
              onChange={(e) => setFilterClass(e.target.value)}
            >
              <MenuItem value=""><em>All Classes</em></MenuItem>
              {classes.map((c) => (
                <MenuItem key={c.id || c.class_id} value={c.id || c.class_id}>
                  {c.class_name || c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value=""><em>All Status</em></MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ width: '100%' }}>
          <DataGrid
            autoHeight
            rows={filteredStructures}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            disableRowSelectionOnClick
            onRowClick={(params) => navigate(`/fees/structure/${params.row.id}/components`)}
            getRowId={(row) => row.id}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </Box>
      </WidgetContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth disableRestoreFocus>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this fee structure? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteId(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
